import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';
import {
  ArrowLeft,
  Calendar,
  Download,
  ChevronDown,
  TrendingUp,
  Receipt,
  ShoppingBag,
  Search,
  ChevronRight,
  ArrowDown,
  ArrowUp,
  Package,
  Store,
  Wallet,
  Coins
} from 'lucide-react';

const formatCurrency = (amount) => {
  const n = (typeof amount === 'string' ? parseFloat(amount) : typeof amount === 'number' ? amount : 0) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
};

const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const toInputDateFormat = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BillReportView = ({ onBack, bills = [] }) => {
  const { t } = useLang();
  const [selectedRange, setSelectedRange] = useState('this_month');
  const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [toDate, setToDate] = useState(new Date());
  const [businessProfile, setBusinessProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);

  // Fetch Business Profile & Items on mount (matching mobile initialization)
  useEffect(() => {
    const initData = async () => {
      try {
        const { getBusinessProfile } = await import('../../services/dashboardApi');
        const res = await getBusinessProfile();
        if (res?.success) setBusinessProfile(res.data);
      } catch (err) {
        console.error('Error fetching biz profile:', err);
      }

      try {
        const allItems = await itemService.getAllItems();
        setItems(Array.isArray(allItems) ? allItems : []);
      } catch (err) {
        console.error('Error fetching items for stock calculation:', err);
      } finally {
        setIsLoadingItems(false);
      }
    };
    initData();
  }, []);

  const handleDownloadPdf = () => {
    window.print();
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setFromDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setToDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const applyDateRange = (type) => {
    if (type === 'custom') {
      setSelectedRange('custom');
      return;
    }
    const now = new Date();
    let from, to;
    switch (type) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'last_week':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        to = now;
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'all':
        from = new Date(2020, 0, 1);
        to = now;
        break;
      default:
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    }
    setFromDate(from);
    setToDate(to);
    setSelectedRange(type);
  };

  const dateRanges = [
    { value: 'custom', label: t(LangKeys.date_range) },
    { value: 'today', label: t(LangKeys.single_day) },
    { value: 'this_month', label: t(LangKeys.this_month) },
    { value: 'last_week', label: t(LangKeys.last_week) },
    { value: 'last_month', label: t(LangKeys.last_month) },
    { value: 'all', label: t(LangKeys.all) },
  ];

  // Calculate COGS using FIFO matching & Stock values (Exact port of Flutter bill_reports_screen.dart)
  const summary = useMemo(() => {
    const fromDateStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const toDateEnd = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59);

    const filteredBills = bills.filter(bill => {
      const billDate = new Date(bill.date);
      return billDate >= fromDateStart && billDate <= toDateEnd;
    });

    const totalSales = filteredBills
      .filter(b => b.type === 'sale')
      .reduce((sum, b) => sum + (parseFloat(b.finalBillAmount) || 0), 0);

    const totalPurchases = filteredBills
      .filter(b => b.type === 'purchase')
      .reduce((sum, b) => sum + (parseFloat(b.finalBillAmount) || 0), 0);

    const totalExpenses = filteredBills
      .filter(b => b.type === 'expense')
      .reduce((sum, b) => sum + (parseFloat(b.finalBillAmount) || 0), 0);

    // FIFO Queues
    const fifoQueues = {};

    // Initialize FIFO queues with opening stock of items
    items.forEach(item => {
      const opStock = parseFloat(item.opening_stock || item.openingStock || 0);
      const pPrice = parseFloat(item.purchase_price || item.purchasePrice || 0);
      if ((item.item_type === 'product' || !item.item_type) && opStock > 0) {
        const itemId = String(item.id || item.name);
        if (!fifoQueues[itemId]) {
          fifoQueues[itemId] = [];
        }
        fifoQueues[itemId].push({ qty: opStock, price: pPrice });
      }
    });

    let openingStockValue = 0;
    let openingStockCalculated = false;
    let totalCogs = 0;

    const sortedBills = [...bills].sort((a, b) => new Date(a.date) - new Date(b.date));

    sortedBills.forEach(bill => {
      const bDate = new Date(bill.date);
      if (bDate > toDateEnd) return;
      if (bill.type === 'expense') return;

      if (bDate >= fromDateStart && !openingStockCalculated) {
        Object.values(fifoQueues).forEach(queue => {
          queue.forEach(batch => {
            openingStockValue += batch.qty * batch.price;
          });
        });
        openingStockCalculated = true;
      }

      const isWithinRange = bDate >= fromDateStart && bDate <= toDateEnd;
      const billItems = Array.isArray(bill.items) ? bill.items : [];

      billItems.forEach(billItem => {
        const itemId = String(billItem.item_id || billItem.item?.id || billItem.itemName || billItem.item?.name || 'unknown');
        const qty = parseFloat(billItem.quantity || 0);
        const total = parseFloat(billItem.total || billItem.amount || 0);

        if (bill.type === 'purchase') {
          if (!fifoQueues[itemId]) {
            fifoQueues[itemId] = [];
          }
          const unitPrice = qty > 0 ? (total / qty) : 0;
          fifoQueues[itemId].push({ qty, price: unitPrice });
        } else if (bill.type === 'sale') {
          let remainingQty = qty;
          let saleCost = 0;
          const queue = fifoQueues[itemId] || [];

          while (remainingQty > 0 && queue.length > 0) {
            const batch = queue[0];
            if (batch.qty <= remainingQty) {
              saleCost += batch.qty * batch.price;
              remainingQty -= batch.qty;
              queue.shift();
            } else {
              saleCost += remainingQty * batch.price;
              batch.qty -= remainingQty;
              remainingQty = 0;
            }
          }

          if (remainingQty > 0) {
            const fallbackPrice = parseFloat(billItem.item?.purchase_price || billItem.item?.purchasePrice || 0);
            saleCost += remainingQty * fallbackPrice;
          }

          if (isWithinRange) {
            totalCogs += saleCost;
          }
        }
      });
    });

    if (!openingStockCalculated) {
      Object.values(fifoQueues).forEach(queue => {
        queue.forEach(batch => {
          openingStockValue += batch.qty * batch.price;
        });
      });
      openingStockCalculated = true;
    }

    let stockValue = 0;
    Object.values(fifoQueues).forEach(queue => {
      queue.forEach(batch => {
        stockValue += batch.qty * batch.price;
      });
    });

    const netProfit = totalSales - (totalCogs + totalExpenses);

    return {
      totalSales,
      totalPurchases,
      totalExpenses,
      openingStockValue,
      stockValue,
      netProfit,
      filtered: filteredBills
    };
  }, [bills, items, fromDate, toDate]);

  const isProfit = summary.netProfit >= 0;

  return (
    <div className="print-main-wrapper" style={styles.container}>
      {/* Top Header Bar */}
      <div className="no-print" style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} className="no-print" style={styles.backBtn}>
            <ArrowLeft size={20} />
          </button>
          <h3 style={styles.title}>{t(LangKeys.bill_report_title)}</h3>
        </div>
      </div>

      <div className="no-print" style={styles.scrollContent}>
        {/* 1. FILTER CARD */}
        <div style={styles.filterCard}>
          <div style={styles.dateRow}>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(fromDate)}
                onChange={handleFromDateChange}
                style={styles.dateInput}
              />
            </div>
            <span style={styles.toLabel}>{t(LangKeys.to)}</span>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(toDate)}
                onChange={handleToDateChange}
                style={styles.dateInput}
              />
            </div>
          </div>
          <div className="no-print" style={styles.dropdownBox}>
            <select
              style={styles.select}
              value={selectedRange}
              onChange={(e) => applyDateRange(e.target.value)}
            >
              {dateRanges.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown size={18} color="#64748B" />
          </div>
        </div>

        {/* 2. SUMMARY GRID: 6 Summary Badges (Matching Flutter mobile layout) */}
        <div style={styles.summaryCardWrapper}>
          <div style={styles.summaryGrid}>
            {/* 1. Opening Stock */}
            <div style={{ ...styles.summaryItem, backgroundColor: '#EEF2FF', borderColor: 'rgba(79, 70, 229, 0.2)' }}>
              <div style={{ ...styles.iconCircle, backgroundColor: 'rgba(79, 70, 229, 0.12)' }}>
                <Package size={18} color="#4F46E5" />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>{t(LangKeys.opening_stock)}</span>
                <span style={{ ...styles.summaryValue, color: '#4F46E5' }}>{formatCurrency(summary.openingStockValue)}</span>
              </div>
            </div>

            {/* 2. Total Purchases */}
            <div style={{ ...styles.summaryItem, backgroundColor: '#FEF3C7', borderColor: 'rgba(217, 119, 6, 0.2)' }}>
              <div style={{ ...styles.iconCircle, backgroundColor: 'rgba(217, 119, 6, 0.12)' }}>
                <ShoppingBag size={18} color="#D97706" />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>{t(LangKeys.total_purchases)}</span>
                <span style={{ ...styles.summaryValue, color: '#D97706' }}>{formatCurrency(summary.totalPurchases)}</span>
              </div>
            </div>

            {/* 3. Total Sales */}
            <div style={{ ...styles.summaryItem, backgroundColor: '#EFF6FF', borderColor: 'rgba(29, 78, 216, 0.2)' }}>
              <div style={{ ...styles.iconCircle, backgroundColor: 'rgba(29, 78, 216, 0.12)' }}>
                <TrendingUp size={18} color="#1D4ED8" />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>{t(LangKeys.total_sales)}</span>
                <span style={{ ...styles.summaryValue, color: '#1D4ED8' }}>{formatCurrency(summary.totalSales)}</span>
              </div>
            </div>

            {/* 4. Total Expenses */}
            <div style={{ ...styles.summaryItem, backgroundColor: '#FEF2F2', borderColor: 'rgba(220, 38, 38, 0.2)' }}>
              <div style={{ ...styles.iconCircle, backgroundColor: 'rgba(220, 38, 38, 0.12)' }}>
                <Receipt size={18} color="#DC2626" />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>{t(LangKeys.total_expenses)}</span>
                <span style={{ ...styles.summaryValue, color: '#DC2626' }}>{formatCurrency(summary.totalExpenses)}</span>
              </div>
            </div>

            {/* 5. Stock Value */}
            <div style={{ ...styles.summaryItem, backgroundColor: '#F0FDFA', borderColor: 'rgba(13, 148, 136, 0.2)' }}>
              <div style={{ ...styles.iconCircle, backgroundColor: 'rgba(13, 148, 136, 0.12)' }}>
                <Store size={18} color="#0D9488" />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>{t(LangKeys.stock_value)}</span>
                <span style={{ ...styles.summaryValue, color: '#0D9488' }}>{formatCurrency(summary.stockValue)}</span>
              </div>
            </div>

            {/* 6. Net Profit / Net Loss */}
            <div style={{
              ...styles.summaryItem,
              backgroundColor: isProfit ? '#F0FDF4' : '#FEF2F2',
              borderColor: isProfit ? 'rgba(22, 163, 74, 0.2)' : 'rgba(220, 38, 38, 0.2)'
            }}>
              <div style={{
                ...styles.iconCircle,
                backgroundColor: isProfit ? 'rgba(22, 163, 74, 0.12)' : 'rgba(220, 38, 38, 0.12)'
              }}>
                <Wallet size={18} color={isProfit ? '#16A34A' : '#DC2626'} />
              </div>
              <div style={styles.summaryTextCol}>
                <span style={styles.summaryLabel}>
                  {isProfit ? t(LangKeys.net_profit) : t(LangKeys.net_loss)}
                </span>
                <span style={{ ...styles.summaryValue, color: isProfit ? '#16A34A' : '#DC2626' }}>
                  {formatCurrency(summary.netProfit)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. TRANSACTIONS LIST */}
        <div style={styles.listSection}>
          {summary.filtered.length === 0 ? (
            <div style={styles.emptyState}>
              <Search size={48} color="#CBD5E1" />
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748B' }}>
                {t(LangKeys.no_transactions_found_period)}
              </p>
            </div>
          ) : (
            summary.filtered.map((bill, i) => {
              let icon = <ArrowDown size={20} />;
              let color = '#16A34A';
              let title = bill.customer?.customerName || bill.customer_name || t(LangKeys.customer);

              if (bill.type === 'purchase') {
                icon = <ShoppingBag size={20} />;
                color = '#EA580C';
                title = bill.customer?.customerName || bill.supplier_name || t(LangKeys.supplier);
              } else if (bill.type === 'expense') {
                icon = <ArrowUp size={20} />;
                color = '#DC2626';
                title = bill.expenseCategory ? t(bill.expenseCategory) : t(LangKeys.expense_fallback_category);
              }

              return (
                <div key={i} style={styles.txnTile}>
                  <div style={{ ...styles.txnIconCircle, backgroundColor: `${color}18`, color }}>
                    {React.cloneElement(icon, { color, size: 20 })}
                  </div>
                  <div style={styles.txnInfo}>
                    <span style={styles.txnTitle}>{title}</span>
                    <span style={styles.txnDate}>{formatDateTime(bill.date)}</span>
                  </div>
                  <div style={styles.txnRight}>
                    <span style={{ ...styles.txnAmount, color }}>{formatCurrency(bill.finalBillAmount)}</span>
                    <ChevronRight size={16} color="#CBD5E1" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 4. BOTTOM DOWNLOAD PDF BUTTON */}
      {summary.filtered.length > 0 && (
        <div className="no-print" style={styles.bottomBar}>
          <button onClick={handleDownloadPdf} style={styles.downloadBtn}>
            <Download size={18} />
            <span>{t(LangKeys.download_pdf)}</span>
          </button>
        </div>
      )}

      {/* 5. PRINTABLE PDF REPORT SHEET */}
      <div className="print-only" id="bill-report-pdf-template" style={styles.printSheet}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center' }}>
            {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="" />}
          </div>
          <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{businessProfile?.business_name || t(LangKeys.my_business)}</h2>
            {businessProfile?.owner_name && <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>{t(LangKeys.name)}: {businessProfile.owner_name}</div>}
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
          </div>
          <div style={{ width: '60px', height: '60px' }} />
        </div>

        <div style={{ background: '#1D4ED8', color: 'white', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '20px 0', borderRadius: '6px', textTransform: 'uppercase' }}>
          {t(LangKeys.bill_report_title)}
        </div>

        <div style={{ textAlign: 'center', fontSize: '12px', color: '#475569', marginBottom: '20px', fontWeight: '600' }}>
          ({formatDate(fromDate)} - {formatDate(toDate)})
        </div>

        {/* Printable Summary Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '25px' }}>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: '#EEF2FF' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>{t(LangKeys.opening_stock)}</span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#4F46E5' }}>{formatCurrency(summary.openingStockValue)}</span>
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: '#FEF3C7' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>{t(LangKeys.total_purchases)}</span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#D97706' }}>{formatCurrency(summary.totalPurchases)}</span>
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: '#EFF6FF' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>{t(LangKeys.total_sales)}</span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#1D4ED8' }}>{formatCurrency(summary.totalSales)}</span>
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: '#FEF2F2' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>{t(LangKeys.total_expenses)}</span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#DC2626' }}>{formatCurrency(summary.totalExpenses)}</span>
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: '#F0FDFA' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>{t(LangKeys.stock_value)}</span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: '#0D9488' }}>{formatCurrency(summary.stockValue)}</span>
          </div>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px', textAlign: 'center', backgroundColor: isProfit ? '#F0FDF4' : '#FEF2F2' }}>
            <span style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', fontWeight: '700', display: 'block' }}>
              {isProfit ? t(LangKeys.net_profit) : t(LangKeys.net_loss)}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '800', color: isProfit ? '#16A34A' : '#DC2626' }}>
              {formatCurrency(summary.netProfit)}
            </span>
          </div>
        </div>

        <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a', marginBottom: '10px', borderLeft: '4px solid #1D4ED8', paddingLeft: '8px' }}>
          {t(LangKeys.all_transactions)}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #cbd5e1', padding: '8px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '22%' }}>{t(LangKeys.date)?.toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '8px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '16%' }}>{t(LangKeys.type)?.toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '8px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '42%' }}>{t(LangKeys.party_or_details)?.toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '8px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '20%' }}>{t(LangKeys.amount)?.toUpperCase()}</th>
            </tr>
          </thead>
          <tbody>
            {summary.filtered.length > 0 ? summary.filtered.map((bill, i) => {
              let title = bill.customer?.customerName || bill.customer_name || t(LangKeys.customer);
              if (bill.type === 'expense') {
                title = bill.expenseCategory ? t(bill.expenseCategory) : t(LangKeys.expense_fallback_category);
              } else if (bill.type === 'purchase') {
                title = bill.customer?.customerName || bill.supplier_name || t(LangKeys.supplier);
              }
              return (
                <tr key={i}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', color: '#334155' }}>
                    {formatDate(bill.date)}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold', color: bill.type === 'sale' ? '#16a34a' : bill.type === 'purchase' ? '#ea580c' : '#dc2626' }}>
                    {bill.type === 'sale' ? (t(LangKeys.sale)?.toUpperCase()) : bill.type === 'purchase' ? (t(LangKeys.purchase)?.toUpperCase()) : (t(LangKeys.expense)?.toUpperCase())}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', color: '#334155' }}>
                    {title}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', color: '#334155', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(bill.finalBillAmount)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '24px', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
                  {t(LangKeys.no_transactions_found_period)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: '30px', borderTop: '1px solid #e2e8f0', paddingTop: '12px', textAlign: 'center', fontSize: '10px', color: '#94a3b8', fontWeight: '600' }}>
          {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: '#F3F4F6'
  },
  header: {
    padding: '16px 20px',
    background: '#1D4ED8',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(29, 78, 216, 0.25)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  backBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  title: {
    fontSize: '18px',
    fontWeight: '800',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  scrollContent: {
    flex: 1,
    padding: '14px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  filterCard: {
    background: '#FFFFFF',
    padding: '16px',
    borderRadius: '18px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  dateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  dateChip: {
    flex: 1,
    height: '44px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600'
  },
  dateInput: {
    border: 'none',
    background: 'transparent',
    fontWeight: '600',
    fontSize: '14px',
    color: '#1F2937',
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  toLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#6B7280'
  },
  dropdownBox: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '14px',
    height: '44px',
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center'
  },
  select: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontWeight: '700',
    fontSize: '14px',
    color: '#1F2937',
    cursor: 'pointer'
  },
  summaryCardWrapper: {
    background: '#FFFFFF',
    padding: '14px',
    borderRadius: '18px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '10px'
  },
  summaryItem: {
    padding: '12px 14px',
    borderRadius: '16px',
    borderWidth: '1.2px',
    borderStyle: 'solid',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
  },
  iconCircle: {
    width: '38px',
    height: '38px',
    borderRadius: '19px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  summaryTextCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflow: 'hidden'
  },
  summaryLabel: {
    fontSize: '11.5px',
    fontWeight: '700',
    color: '#4B5563',
    whiteSpace: 'nowrap'
  },
  summaryValue: {
    fontSize: '17px',
    fontWeight: '900',
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap'
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  txnTile: {
    background: '#FFFFFF',
    padding: '14px 16px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
    transition: 'transform 0.1s, box-shadow 0.1s'
  },
  txnIconCircle: {
    width: '42px',
    height: '42px',
    borderRadius: '21px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  txnInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  txnTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1F2937'
  },
  txnDate: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500'
  },
  txnRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  txnAmount: {
    fontSize: '15px',
    fontWeight: '800'
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#9CA3AF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    background: '#FFFFFF',
    borderRadius: '18px'
  },
  bottomBar: {
    padding: '14px 18px',
    background: '#FFFFFF',
    borderTop: '1px solid #E5E7EB',
    boxShadow: '0 -4px 12px rgba(0,0,0,0.03)'
  },
  downloadBtn: {
    width: '100%',
    height: '48px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: '#0F5BD8',
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(15, 91, 216, 0.25)',
    transition: 'opacity 0.15s'
  },
  printSheet: {
    padding: '10mm 15mm',
    boxSizing: 'border-box',
    background: 'white',
    fontFamily: "'Outfit', 'Hind', sans-serif",
    color: '#1e293b',
    textAlign: 'left',
    width: '100%'
  }
};

export default BillReportView;

